import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { getTextColor, hasChildSelected, TextMenu } from "..";
import { useEffect, useState } from "react";
import { ArrowDownIcon, IconWrapper } from "../icon";
import { defaultAnimationDuration, sideBarIconSize } from "common/const/const";
import styled from "styled-components";
import { colors } from "common/const/colors";

export const SideBarItem = ({
  item,
  onClick,
  selectedKey,
  isOpen,
  level = 0,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { key, icon: IconComponent, label, link, children } = item;
  const expandable = Array.isArray(children) && children.length > 0;

  const isSelected =
    link === location.pathname ||
    selectedKey === key ||
    hasChildSelected(children, selectedKey, location);

  const [expanded, setExpanded] = useState(isOpen && isSelected);

  useEffect(() => {
    setExpanded(isOpen && isSelected);
  }, [isOpen, isSelected]);

  return (
    <>
      <SideBarItemWrapper
        $isOpen={isOpen}
        onClick={(e) => {
          e.stopPropagation();
          if (link)
            navigate(link, { replace: true, state: { refresh: Date.now() } });
          onClick?.(key, expandable);
          if (expandable) setExpanded((prev) => !prev);
        }}>
        <IconWrapper size={isOpen ? "1.25rem" : "1.5rem"}>
          {IconComponent && (
            <IconComponent color={isSelected && colors.MainBlue} />
          )}
        </IconWrapper>
        <TextContent $isOpen={isOpen}>
          <MenuLabel color={getTextColor({ isSelected })}>
            {t(`sideBar.${label}`)}
          </MenuLabel>
        </TextContent>
        <ArrowContent $isOpen={isOpen}>
          {expandable && (
            <IconWrapper>
              <ArrowDownIcon
                style={{
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: `transform ${defaultAnimationDuration} ease`,
                }}
              />
            </IconWrapper>
          )}
        </ArrowContent>
      </SideBarItemWrapper>
      {expandable && (
        <ExpandWrapper className={expanded ? "expanded" : ""}>
          {children.map((child) => (
            <SideBarItem
              key={child.key}
              item={child}
              onClick={onClick}
              selectedKey={selectedKey}
              level={level + 1}
              isOpen={isOpen}
            />
          ))}
        </ExpandWrapper>
      )}
    </>
  );
};

export const SideBarItemWrapper = styled.li`
  box-sizing: border-box;
  display: flex;
  gap: ${({ $isOpen }) => ($isOpen ? "1rem" : "0")};
  width: 100%;
  min-height: 3rem;
  padding: 0 1rem;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: gap ${defaultAnimationDuration} ease;
`;

const TextContent = styled.span`
  display: flex;
  align-items: center;
  flex-grow: 1;
  overflow: hidden;
  min-width: 0;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  max-width: ${({ $isOpen }) => ($isOpen ? "15rem" : "0")};
  transition:
    opacity ${defaultAnimationDuration} ease,
    max-width ${defaultAnimationDuration} ease;
`;

// 한 줄에 안 들어가는 라벨은 최대 2줄까지 줄바꿈
const MenuLabel = styled(TextMenu)`
  width: 100%;
  line-height: 1.3;
  white-space: normal;
  word-break: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`;

const ArrowContent = styled.span`
  display: flex;
  align-items: center;
  overflow: hidden;
  min-width: 0;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  max-width: ${({ $isOpen }) => ($isOpen ? "2rem" : "0")};
  transition:
    opacity ${defaultAnimationDuration} ease,
    max-width ${defaultAnimationDuration} ease;
`;

const ExpandWrapper = styled.div`
  overflow: hidden;
  width: 100%;
  max-height: 0;
  transition: max-height ${defaultAnimationDuration} ease-in-out;
  will-change: max-height;
  cursor: pointer;

  &.expanded {
    max-height: 100vh;
  }
`;
